import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useEvents } from '../context/EventsContext';
import { useYouTubeData } from '../context/YouTubeContext';
import { useEventTransition } from './EventTransition';
import MinecraftBlockCountdown from './MinecraftBlockCountdown';
import VideoCarousel from './VideoCarousel';
import DecorativeLeaves from './DecorativeLeaves';
import { spriteMap, SPRITE_SIZE } from '../data/itemSpriteMap';
import { renderMinecraftTextHTML } from '../utils/minecraftIcons';
import './Hero.css';

const PARTYKIT_HOST = process.env.REACT_APP_PARTYKIT_HOST || 'thetopia-live.lafless.partykit.dev';

/* ── Event Icon (reused from EventManagement pattern) ── */
function HeroEventIcon({ item, size = 48 }) {
  if (!item) return null;
  const pos = spriteMap[item];
  if (!pos) return null;
  const scale = size / SPRITE_SIZE;
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundImage: 'url(/img/item-sprites.png)',
        backgroundPosition: `-${pos[0] * scale}px -${pos[1] * scale}px`,
        backgroundSize: `${1248 * scale}px ${1248 * scale}px`,
        imageRendering: 'pixelated',
        flexShrink: 0,
      }}
    />
  );
}

export default function Hero() {
  const { events, loading } = useEvents();
  const { playlistVideos, playlistLoading } = useYouTubeData();
  const triggerTransition = useEventTransition();

  // Find a live event (has slug + webAddon = it's interactive)
  // Only show if the event starts within 5 minutes or has already started
  const liveEvent = events?.find(e => {
    if (!e.slug || !e.webAddon) return false;
    if (!e.timestamp) return true; // no scheduled time = always show
    const startsSec = typeof e.timestamp === 'number' && e.timestamp > 1e12 ? e.timestamp / 1000 : e.timestamp;
    return startsSec <= (Date.now() / 1000) + 300;
  });

  // Poll viewer count from PartyKit
  const [viewerCount, setViewerCount] = useState(null);
  useEffect(() => {
    if (!liveEvent?.slug || !liveEvent?.webAddon) return;

    const partyPath = liveEvent.webAddon === 'chunk-delete'
      ? `/parties/chunk/${liveEvent.slug}`
      : liveEvent.webAddon === 'block-delete'
        ? `/parties/block/${liveEvent.slug}`
        : `/party/${liveEvent.slug}`;

    const fetchCount = () => {
      fetch(`https://${PARTYKIT_HOST}${partyPath}`)
        .then(r => { if (r.ok) return r.json(); })
        .then(d => { if (d && typeof d.connections === 'number') setViewerCount(d.connections); })
        .catch(() => {});
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);

    // Pause polling when tab is hidden
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchCount();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [liveEvent?.slug, liveEvent?.webAddon]);

  // Get the next upcoming event (for countdown)
  const nextEvent = events?.[0];
  const nextEventTimestamp = nextEvent?.timestamp;

  return (
    <section className="hero">
      <DecorativeLeaves config="hero" />
      <div className="hero-container">
        <div className="hero-content">
          <p className="hero-subtitle animate-slide-up delay-1">
            A Minecraft Event network tailored with fun, competitive, and creative experiences! With no queue priority, everyone has the same chance at participating.
          </p>

          <div className="hero-actions animate-slide-up delay-2">
            <Link to="/about" className="btn btn-white btn-large">
              Learn More
            </Link>
            {liveEvent && (
              <button onClick={() => triggerTransition(`/event/${liveEvent.slug}`)} className="btn btn-live btn-large">
                <span className="btn-live-dot" />
                Join Live Event
              </button>
            )}
          </div>
        </div>

        <div className="hero-clock-section animate-slide-up delay-1">
          <div className="countdown-wrapper">
            {liveEvent ? (
              <div className="countdown-reveal">
                <button onClick={() => triggerTransition(`/event/${liveEvent.slug}`)} className="hero-live-card">
                  <div className="hero-live-badge">
                    <span className="hero-live-dot" />
                    LIVE NOW
                    {viewerCount > 0 && (
                      <span className="hero-live-viewers">{viewerCount} watching</span>
                    )}
                  </div>
                  <div className="hero-live-body">
                    {liveEvent.item && <HeroEventIcon item={liveEvent.item} size={56} />}
                    <div className="hero-live-info">
                      <h3 className="hero-live-name" dangerouslySetInnerHTML={{ __html: renderMinecraftTextHTML(liveEvent.name || 'Live Event') }} />
                      {liveEvent.description && (
                        <p className="hero-live-desc">{liveEvent.description.split('/n')[0]}</p>
                      )}
                    </div>
                    <svg className="hero-live-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </button>
              </div>
            ) : loading || (!nextEventTimestamp && playlistLoading) ? null : (
              <div className="countdown-reveal">
                {nextEventTimestamp ? (
                  <>
                    <MinecraftBlockCountdown timestamp={nextEventTimestamp} eventName={nextEvent?.name} />
                    <Link to="/events" className="view-all-events-btn">
                      <span className="view-all-events-text">View all events</span>
                      <span className="plus-icon"></span>
                    </Link>
                  </>
                ) : playlistVideos.length > 0 ? (
                  <VideoCarousel videos={playlistVideos} />
                ) : (
                  <>
                    <div className="countdown-simple">
                      <div className="countdown-label">Next Event</div>
                      <div className="countdown-time">Coming Soon</div>
                    </div>
                    <Link to="/events" className="view-all-events-btn">
                      <span className="view-all-events-text">View all events</span>
                      <span className="plus-icon"></span>
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

    </section>
  );
}
